<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div>
        <h1 class="mt-2 text-3xl text-gray-900">Faturamento</h1>
        <p class="mt-2 max-w-2xl text-sm text-gray-500">Consulte os valores de faturamento por período e centro de custo.</p>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <form>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-6">
              <div class="flex flex-col gap-1">
                <label for="de" class="text-sm font-medium text-gray-500">A partir de</label>
                <input
                  v-model="de"
                  type="date"
                  name="de"
                  id="de"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label for="ate" class="text-sm font-medium text-gray-500">Até</label>
                <input
                  v-model="ate"
                  type="date"
                  name="ate"
                  id="ate"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label for="centrocusto-select" class="text-sm font-medium text-gray-500">Centro de custo</label>

                <div class="relative group">
                  <select
                    v-model="centrocusto"
                    id="centrocusto-select"
                    name="centrocusto"
                    class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
                  >
                    <option :value="null">Todos</option>
                    <option v-for="centro in centroscusto" :key="centro._id" :value="centro._id">
                      {{ centro.nome }}
                    </option>
                  </select>

                  <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div class="flex items-end">
                <button
                  @click="start"
                  type="button"
                  class="h-11 w-full rounded-xl font-medium text-white transition"
                  :style="{ backgroundColor: primaryColor }"
                  @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                  @mouseleave="$event.target.style.backgroundColor = primaryColor"
                >
                  Gerar relatório
                </button>
              </div>

              <div class="flex flex-col gap-1">
                <label for="email" class="text-sm font-medium text-gray-500">Email download relatório</label>
                <input
                  v-model="email"
                  type="email"
                  name="email"
                  id="email"
                  placeholder="email@exemplo.com"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex items-end">
                <button
                  @click="downloadRelatorio"
                  type="button"
                  class="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                >
                  <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v12m0 0l-3.5-3.5M12 15l3.5-3.5M4 21h16" />
                  </svg>
                  Download relatório
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div v-if="progresso !== 100" class="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm font-medium text-gray-700 shadow-sm">
        Progressão:
        <span class="font-semibold text-green-600">{{ progresso }}%</span>
        / 100%
      </div>

      <div v-else class="rounded-2xl border border-green-200 bg-green-50 px-5 py-4 text-sm font-medium text-green-700">Relatório gerado com sucesso.</div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">Campanhas envio massa</p>
          <p class="mt-2 text-2xl font-medium text-green-700">
            {{ faturamento.totalMassa }}
          </p>

          <p v-if="$store.state.user.lzv2.faturamento.visivel" class="mt-1 text-sm text-gray-500">
            x {{ numberFormat($store.state.user.lzv2.faturamento.envio) }}
            =
            <span class="font-medium text-green-600">
              {{ formatPrice(faturamento.valorMassa) }}
            </span>
          </p>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">Conversas ativas</p>
          <p class="mt-2 text-2xl font-medium text-blue-700">
            {{ faturamento.totalChats }}
          </p>

          <p v-if="$store.state.user.lzv2.faturamento.visivel" class="mt-1 text-sm text-gray-500">
            x {{ numberFormat($store.state.user.lzv2.faturamento.chat) }}
            =
            <span class="font-medium text-green-600">
              {{ formatPrice(faturamento.valorChats) }}
            </span>
          </p>
        </div>

        <div
          v-if="!$store.state.user.lzv2.faturamento.visivel || ($store.state.user.lzv2.faturamento.visivel && $store.state.user.lzv2.faturamento.operador > 1)"
          class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
        >
          <p class="text-sm font-medium text-gray-500">Operadores ativos</p>
          <p class="mt-2 text-2xl font-medium text-indigo-700">
            {{ faturamento.totalOperadores }}
          </p>

          <p v-if="$store.state.user.lzv2.faturamento.visivel" class="mt-1 text-sm text-gray-500">
            x {{ numberFormat($store.state.user.lzv2.faturamento.operador) }}
            =
            <span class="font-medium text-green-600">
              {{ formatPrice(faturamento.valorOperadores) }}
            </span>
          </p>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">Validação Whatsapp</p>
          <p class="mt-2 text-2xl font-medium text-yellow-600">
            {{ faturamento.totalValidacao }}
          </p>

          <p v-if="$store.state.user.lzv2.faturamento.visivel" class="mt-1 text-sm text-gray-500">
            x R$ 0,01 =
            <span class="font-medium text-green-600">
              {{ formatPrice(faturamento.valorValidacao) }}
            </span>
          </p>
        </div>

        <div v-if="faturamento.totalExcepcional > 0" class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">Transferência de números</p>
          <p class="mt-2 text-2xl font-medium text-purple-700">
            {{ faturamento.totalExcepcional }}
          </p>

          <p v-if="$store.state.user.lzv2.faturamento.visivel" class="mt-1 text-sm text-gray-500">
            x {{ numberFormat(faturamento.valorUnitarioExcepcional) }}
            =
            <span class="font-medium text-green-600">
              {{ formatPrice(faturamento.valorExcepcional) }}
            </span>
          </p>
        </div>
      </div>

      <div v-if="$store.state.user.lzv2.faturamento.visivel" class="max-w-xs rounded-2xl border border-green-200 bg-green-50 px-5 py-4 shadow-sm">
        <p class="text-sm font-medium text-green-700">Total</p>
        <p class="mt-1 text-xl font-medium text-green-700">
          {{ formatPrice(faturamento.total) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  data() {
    return {
      breadcrumb: [
        {
          url: "/relatorios/faturamento",
          value: "Faturamento"
        }
      ],
      centroscusto: [],
      route: "auditoria",
      de: moment().startOf("month").format("YYYY-MM-DD"),
      ate: moment().endOf("month").format("YYYY-MM-DD"),
      centrocusto: null,
      email: "",
      progresso: 0,
      faturamento: {
        totalMassa: 0,
        totalChats: 0,
        totalOperadores: 0,
        totalValidacao: 0,
        valorMassa: 0,
        valorChats: 0,
        valorOperadores: 0,
        valorValidacao: 0,
        totalExcepcional: 0,
        valorUnitarioExcepcional: 0,
        valorExcepcional: 0,
        total: 0
      }
    };
  },
  mounted() {
    this.sockets.subscribe(`progressaoFaturamento_${this.$store.state.user._id}`, (progresso) => {
      this.progresso = progresso;
    });
  },
  methods: {
    async start() {
      const faturamentoReq = await this.$http.post("/v1/campanhasLZV2/faturamento", {
        de: this.de,
        ate: this.ate,
        centrocusto: this.centrocusto
      });

      this.faturamento = faturamentoReq.data;
    },

    async downloadRelatorio() {
      const faturamentoReq = await this.$http.post("/v1/campanhasLZV2/faturamento/dowload", {
        de: this.de,
        ate: this.ate,
        email: this.email,
        centrocusto: this.centrocusto
      });

      if (faturamentoReq.data.success) {
        this.$vToastify.success(faturamentoReq.data.msg);
        this.email = "";
        return;
      }

      this.$vToastify.error(faturamentoReq.data.err);
    },

    numberFormat(value) {
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    }
  },
  async beforeMount() {
    const centroscustoReq = await this.$http.post("/v1/centrocusto/list", {
      all: true
    });

    this.centroscusto = centroscustoReq.data.data || [];

    if (this.$store.state.user.centrocusto) {
      this.centrocusto = this.$store.state.user.centrocusto;
    }

    await this.start();
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#centrocusto-select::-ms-expand {
  display: none;
}

#centrocusto-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
